
import React from 'react';
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export interface DataPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

export function DataPagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  className,
}: DataPaginationProps) {
  // Generate page numbers to display
  const getVisiblePages = () => {
    const pages = [];
    
    // Always show first page
    pages.push(1);
    
    // Add ellipsis and nearby pages
    if (currentPage > 3) {
      pages.push('ellipsis1');
    }
    
    // Add pages around current
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    
    // Add ellipsis before last page
    if (currentPage < totalPages - 2) {
      pages.push('ellipsis2');
    }
    
    // Always add last page if there is more than one page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Simple singular/plural helper
  const itemsLabel = totalItems === 1 ? "item" : "items";
  
  // Always render container; only hide page navigation for single page
  const showPageNavigation = totalPages > 1;

  return (
    <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t ${className}`}>
      {/* Left side - Page navigation */}
        {showPageNavigation && (
          <div className="flex items-center order-2 sm:order-1">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={currentPage === 1 ? "opacity-50" : "cursor-pointer"}
                    aria-disabled={currentPage === 1}
                  />
                </PaginationItem>

                {getVisiblePages().map((page, index) => 
                  typeof page === 'number' ? (
                    <PaginationItem key={`page-${page}`} className="hidden sm:inline-flex">
                      <PaginationLink 
                        isActive={page === currentPage}
                        onClick={() => onPageChange(page)}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={`ellipsis-${index}`} className="hidden sm:inline-flex">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                )}

                <PaginationItem className="sm:hidden">
                  <span className="text-sm text-muted-foreground px-2">
                    {currentPage} / {totalPages}
                  </span>
                </PaginationItem>

                <PaginationItem>
                  <PaginationNext 
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={currentPage === totalPages ? "opacity-50" : "cursor-pointer"}
                    aria-disabled={currentPage === totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

      {/* Right side - Rows per page and item count */}
      <div className="flex items-center gap-4 sm:gap-6 text-sm text-muted-foreground order-1 sm:order-2">
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline">Rows per page:</span>
          <span className="sm:hidden">Rows:</span>
          <Select 
            value={pageSize.toString()} 
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(size => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {totalItems !== undefined && (
          <span>
            {totalItems} {itemsLabel}
          </span>
        )}
      </div>
    </div>
  );
}
